import { apiService } from "../services/api";
import { getParentIdFromFormData } from "../state/utils/configUtils";

/** Lookups that filter by service_details.id (inspection parent), not jobcard id. */
export const INVOICE_SERVICE_PARENT_LOOKUPS = new Set([
  "invoice_items",
  "invoice_items_pending",
  "invoice_labour",
  "invoice_miscellaneous",
]);

/**
 * Invoice submodules need service_details.id as parentid (same as inspection workflow).
 * Dropdown may store jobcard id in parentid while selectedData.id is the service id.
 */
export const resolveInvoiceLookupParentId = (
  formData: Record<string, any>,
  parentField?: string
): string | number | null => {
  if (!formData) return null;

  const field = parentField || "parentid";
  const selected =
    formData[`${field}_selectedData`] ??
    formData.parentid1_selectedData ??
    formData.parentid_selectedData ??
    formData.parent_id_selectedData;

  if (selected && typeof selected === "object") {
    const serviceId =
      selected.parent_id ??
      selected.service_details_id ??
      selected.inspection_id ??
      selected.vehicle_in_id;
    if (serviceId !== undefined && serviceId !== null && serviceId !== "") {
      return serviceId;
    }
    if (selected.id !== undefined && selected.id !== null && selected.id !== "") {
      return selected.id;
    }
  }

  const explicit =
    formData.parent_id ?? formData[field] ?? formData.parentid;
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    return explicit;
  }

  return getParentIdFromFormData(formData, field);
};

const extractLookupRows = (response: any): any[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.success && Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

const fetchUtilityLookup = async (
  lookupName: string,
  parentId: string
): Promise<any[]> => {
  try {
    const response = await apiService.post<any>(
      `/pabws/utility/${lookupName}`,
      { parentid: parentId, lookuptype: "" },
      { includeAuthToken: true, errorHandling: "return-empty" }
    );
    return extractLookupRows(response);
  } catch {
    return [];
  }
};

type SpareLookupHit = { unit: number; line: number; rate: number };

const resolveSpareRate = (row: Record<string, any>): number => {
  const rate = Number(row.rate ?? row.txt_rate ?? NaN);
  return Number.isFinite(rate) ? rate : 0;
};

/** True when the API/row already set a discount field (including explicit 0). */
const hasExplicitDiscount = (row: Record<string, any>): boolean => {
  for (const key of [
    "unit_discount",
    "txt_unit_discount",
    "line_discount",
    "txt_line_discount",
  ]) {
    const v = row[key];
    if (v !== undefined && v !== null && v !== "") {
      return true;
    }
  }
  return false;
};

const toSpareHit = (r: Record<string, any>): SpareLookupHit => {
  const unit = Number(r.unit_discount ?? 0);
  const qty = Number(r.qty ?? r.req_qty ?? 1);
  const line = Number(
    r.line_discount ?? (Number.isFinite(unit) ? unit * qty : 0)
  );
  const rate = Number(r.rate ?? 0);
  return {
    unit: Number.isFinite(unit) ? unit : 0,
    line: Number.isFinite(line) ? line : 0,
    rate: Number.isFinite(rate) ? rate : 0,
  };
};

/**
 * Parts-issue rows often store amount but not rate (parts issue screen omits rate).
 * Jobcard / estimation spare lookups hold the selling rate and discounts.
 * Prefer est_ref_id / line id; codeid is rate-only fallback so same-part lines
 * do not steal each other's discounts. Never overwrite an explicit 0 discount.
 */
export const enrichInvoiceSpareRowsWithDiscounts = async (
  rows: Record<string, any>[],
  parentId: string | number
): Promise<Record<string, any>[]> => {
  if (!rows.length || parentId === "" || parentId == null) return rows;

  const parentStr = String(parentId);
  const [jobcardSpare, estSpare] = await Promise.all([
    fetchUtilityLookup("jobcard_Spareparts", parentStr),
    fetchUtilityLookup("estimation_Spareparts", parentStr),
  ]);

  const byEstRef = new Map<string, SpareLookupHit>();
  const rateByCode = new Map<string, number>();

  const indexRows = (list: any[]) => {
    for (const r of list) {
      const hit = toSpareHit(r);
      const estRef = r.est_ref_id ?? r.id;
      if (estRef !== undefined && estRef !== null && estRef !== "") {
        const key = String(estRef);
        if (!byEstRef.has(key)) {
          byEstRef.set(key, hit);
        }
      }
      const code = r.codeid ?? r.drp_code;
      if (code !== undefined && code !== null && code !== "" && hit.rate > 0) {
        const codeKey = String(code);
        if (!rateByCode.has(codeKey)) {
          rateByCode.set(codeKey, hit.rate);
        }
      }
    }
  };
  indexRows(estSpare);
  indexRows(jobcardSpare);

  return rows.map((row) => {
    const estRef = row.est_ref_id ?? row.id;
    const code = row.drp_code ?? row.codeid;
    const hitByRef =
      estRef !== undefined && estRef !== null && estRef !== ""
        ? byEstRef.get(String(estRef))
        : undefined;
    const codeRate =
      code !== undefined && code !== null && code !== ""
        ? rateByCode.get(String(code))
        : undefined;

    const needsDiscount = !hasExplicitDiscount(row);
    const needsRate = resolveSpareRate(row) === 0;

    if (!needsDiscount && !needsRate) {
      return row;
    }

    const qty = Number(row.txt_qty ?? row.qty ?? 1);
    const updates: Record<string, any> = { ...row };

    if (needsRate) {
      const rate = hitByRef?.rate && hitByRef.rate > 0 ? hitByRef.rate : codeRate;
      if (rate != null && rate > 0) {
        updates.rate = rate;
        updates.txt_rate = rate;
      }
    }

    // Discount only from the matching estimation/jobcard line — never by codeid alone.
    if (needsDiscount && hitByRef && (hitByRef.unit > 0 || hitByRef.line > 0)) {
      const unit = hitByRef.unit;
      const line = hitByRef.line > 0 ? hitByRef.line : unit * qty;
      updates.unit_discount = unit;
      updates.txt_unit_discount = unit;
      updates.line_discount = line;
      updates.txt_line_discount = line;
    }

    return updates;
  });
};
